/**
 * Toast notification manager
 */

export class ToastManager {
    constructor() {
        this.container = null;
        this.toasts = new Map();
    }
    
    /**
     * Initialize toast container
     */
    init() {
        this.container = document.getElementById('notifications-toast-container');
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.id = 'notifications-toast-container';
            this.container.className = 'position-fixed top-0 end-0 p-3';
            document.body.appendChild(this.container);
        }
    }
    
    /**
     * Show toast notification
     */
    show(notif) {
        if (!this.container) return;
        
        const toastId = `toast-${notif.id}`;
        
        // Create toast element
        const toast = document.createElement('div');
        toast.id = toastId;
        toast.className = 'toast notification-toast';
        toast.setAttribute('role', 'alert');
        
        const bgClass = this._getBgClass(notif.type);
        
        // Check if this is a message reply notification (only for feedback/bug/feature messages)
        // System notifications like "Roles Updated" should not have reply button
        const messageId = this._extractMessageId(notif.message);
        const replyId = this._extractReplyId ? this._extractReplyId(notif.message) : null;
        const isMessageReply = notif.title && (
            notif.title.toLowerCase().includes('reply to your') ||
            notif.title.toLowerCase().startsWith('new reply')
        );
        const showReplyBtn = messageId !== null && notif.user_id > 0 && isMessageReply;
        
        // Debug logging
        if (messageId !== null) {
            console.log('[ToastManager] Message notification detected:', {
                messageId,
                title: notif.title,
                isMessageReply,
                user_id: notif.user_id,
                showReplyBtn
            });
        }
        
        // Format message without the message_id and reply_id metadata
        const displayMessage = notif.message ? notif.message.replace(/\n\n__MESSAGE_ID__:\d+(\n__REPLY_ID__:\d+)?$/, '') : '';
        const messageLength = displayMessage.length;
        
        toast.innerHTML = `
            <div class="toast-header ${bgClass} text-canvas pe-0">
                <strong class="me-auto">${this._escapeHtml(notif.title)}</strong>
                <button type="button" class="btn-close notification-close text-canvas" data-close-toast="${notif.id}" aria-label="Close">
                    <i class="bi bi-x-lg text-canvas" aria-hidden="true"></i>
                </button>
            </div>
            <div class="toast-body">
                <div class="notification-preview">${this._truncate(this._formatMessage(displayMessage), 100)}</div>
                ${messageLength > 100 ? '<button class="btn btn-sm btn-link p-0 mt-1" data-expand-toast>Show more</button>' : ''}
                ${showReplyBtn ? `<div class="mt-2 d-flex gap-2">
                    <a href="/messages/${messageId}" class="btn btn-sm btn-outline-secondary" onclick="window.open('/messages/${messageId}', '_blank', 'width=900,height=700'); return false;">
                        <i class="bi bi-eye"></i> <span data-translate="btn_view">Просмотреть</span>
                    </a>
                    <button class="btn btn-sm btn-primary" data-reply-message="${messageId}" data-reply-to="${replyId || ''}">
                        <i class="bi bi-reply"></i> <span data-translate="btn_reply">Ответить</span>
                    </button>
                </div>` : ''}
            </div>
        `;
        
        // Store notification data
        toast.dataset.userId = notif.user_id;
        console.log('[ToastManager] Showing toast for notification:', notif.id, 'user_id:', notif.user_id);
        
        this.container.appendChild(toast);
        
        // Apply translations to newly added elements
        if (window.TranslationApplier && typeof window.TranslationApplier.apply === 'function') {
            window.TranslationApplier.apply();
        }
        
        // Show toast (without Bootstrap animation to avoid flickering)
        toast.classList.add('show');
        
        // Add close button handler
        const closeBtn = toast.querySelector(`[data-close-toast="${notif.id}"]`);
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.closeToast(notif.id);
            });
        }
        
        // Expand on click
        const expandBtn = toast.querySelector('[data-expand-toast]');
        if (expandBtn) {
            expandBtn.addEventListener('click', () => {
                const preview = toast.querySelector('.notification-preview');
                preview.innerHTML = this._formatMessage(displayMessage);
                expandBtn.remove();
            });
        }
        
        // Reply button handler
        const replyBtn = toast.querySelector(`[data-reply-message]`);
        if (replyBtn) {
            replyBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const msgId = parseInt(replyBtn.getAttribute('data-reply-message'), 10);
                const replyToId = replyBtn.getAttribute('data-reply-to') ? parseInt(replyBtn.getAttribute('data-reply-to'), 10) : null;
                this._openReplyModal(msgId, notif.id, replyToId);
            });
        }
        
        // Register action for access-related updates (only if not dismissed)
        // These notifications indicate changes that affect file visibility:
        // - OT Access Updated: OT folder access changed
        // - Roles Updated: user roles changed (affects file filtering)
        // - Organization Role Updated: organization role changed (affects OT access)
        const accessUpdateTitles = ['OT Access Updated', 'Roles Updated', 'Organization Role Updated'];
        if (accessUpdateTitles.includes(notif.title) && !notif.dismissed && window.NotificationManager) {
            console.log('[ToastManager] Registering refresh_files action for access update:', notif.title);
            window.NotificationManager.registerAction('refresh_files', notif.created_at);
        }
        
    }
    
    /**
     * Close toast notification
     */
    closeToast(notificationId) {
        const toast = document.getElementById(`toast-${notificationId}`);
        if (!toast) return;
        
        const userId = parseInt(toast.dataset.userId);
        console.log('[ToastManager] Closing toast, userId:', userId, 'notif.id:', notificationId);
        
        // Remove toast immediately without animation
        toast.remove();
        
        // Call dismiss API for personal notifications
        if (userId > 0 && window.NotificationManager && window.NotificationManager.dismiss) {
            console.log('[ToastManager] Calling dismiss API for personal notification:', notificationId);
            window.NotificationManager.dismiss(notificationId);
        } else {
            console.log('[ToastManager] Skipping dismiss - global notification or NotificationManager not ready');
        }
    }
    
    
    /**
     * Get background class by type
     */
    _getBgClass(type) {
        const map = {
            'info': 'bg-info',
            'success': 'bg-success',
            'warning': 'bg-warning',
            'error': 'bg-error'
        };
        return map[type] || 'bg-info';
    }
    
    /**
     * Truncate text
     */
    _truncate(text, maxLength) {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    }
    
    /**
     * Escape HTML
     */
    _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    /**
     * Format message with line breaks
     */
    _formatMessage(text) {
        const escaped = this._escapeHtml(text);
        return escaped.replace(/\n/g, '<br>');
    }
    
    /**
     * Extract message_id and reply_id from notification message
     * Format: "...\n\n__MESSAGE_ID__:123\n__REPLY_ID__:456"
     */
    _extractMessageId(message) {
        if (!message) return null;
        const match = message.match(/__MESSAGE_ID__:(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    }
    
    _extractReplyId(message) {
        if (!message) return null;
        const match = message.match(/__REPLY_ID__:(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    }
    
    /**
     * Helper function to translate text
     */
    _translate(key) {
        if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
            const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
            const translation = window.TranslationRegistry.translate(key, lang);
            if (translation !== key) return translation;
        }
        return key;
    }
    
    /**
     * Create reply modal if doesn't exist (shared with PanelManager)
     */
    _ensureReplyModal() {
        let modalEl = document.getElementById('messageReplyModal');
        if (modalEl) return modalEl;
        
        const html = `
<div class="modal fade" id="messageReplyModal" tabindex="-1" aria-labelledby="messageReplyModalLabel" aria-hidden="true">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="messageReplyModalLabel" data-translate="messages_reply_modal_title">Ответить на сообщение</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" data-translate-aria-label="messages_close"></button>
      </div>
      <div class="modal-body">
        <div class="mb-3">
          <label for="reply-message-text" class="form-label" data-translate="messages_reply_label">Ваш ответ</label>
          <textarea class="form-control" id="reply-message-text" rows="5" maxlength="5000" required data-translate-placeholder="messages_reply_placeholder"></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" data-translate="feedback_modal_cancel">Отмена</button>
        <button type="button" class="btn btn-primary" id="reply-message-submit" data-translate="feedback_modal_send">Отправить</button>
      </div>
    </div>
  </div>
</div>`;
        
        const div = document.createElement('div');
        div.innerHTML = html;
        document.body.appendChild(div.firstElementChild);
        modalEl = document.getElementById('messageReplyModal');
        
        // Apply translations to modal (with small delay to ensure translations are loaded)
        setTimeout(() => {
            if (window.TranslationApplier && typeof window.TranslationApplier.apply === 'function') {
                window.TranslationApplier.apply();
            }
            // Ensure placeholder is set
            const textarea = modalEl.querySelector('#reply-message-text');
            if (textarea && !textarea.placeholder && window.TranslationRegistry) {
                const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                const placeholder = window.TranslationRegistry.translate('messages_reply_placeholder', lang);
                if (placeholder !== 'messages_reply_placeholder') {
                    textarea.placeholder = placeholder;
                }
            }
        }, 50);
        
        // Setup submit handler
        const submitBtn = modalEl.querySelector('#reply-message-submit');
        if (submitBtn) {
            submitBtn.addEventListener('click', async () => {
                const textarea = document.getElementById('reply-message-text');
                const messageId = modalEl.dataset.messageId;
                
                if (!textarea || !messageId) return;
                
                const content = (textarea.value || '').trim();
                if (!content) {
                    alert(this._translate('messages_reply_required'));
                    return;
                }
                
                submitBtn.disabled = true;
                submitBtn.textContent = this._translate('messages_sending');
                
                try {
                    const replyToId = modalEl.dataset.replyToId ? parseInt(modalEl.dataset.replyToId, 10) : null;
                    const response = await fetch(`/api/messages/${messageId}/reply`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ content: content, reply_to_reply_id: replyToId })
                    });
                    
                    const data = await response.json();
                    
                    if (!response.ok || !data.success) {
                        throw new Error(data.error || this._translate('messages_send_error'));
                    }
                    
                    // Close modal
                    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                        const modal = bootstrap.Modal.getInstance(modalEl);
                        if (modal) modal.hide();
                    }
                    
                    textarea.value = '';
                    
                    // Show success toast (reuse feedback modal toast function)
                    this._showSuccessToast(this._translate('messages_reply_sent'), this._translate('messages_reply_sent_success'));
                    
                    // Dismiss the notification that triggered this reply
                    const notificationId = modalEl.dataset.notificationId;
                    if (notificationId && window.NotificationManager && window.NotificationManager.dismiss) {
                        window.NotificationManager.dismiss(parseInt(notificationId, 10));
                        // Close toast if still visible
                        this.closeToast(parseInt(notificationId, 10));
                    }
                    
                } catch (error) {
                    console.error('[ToastManager] Error sending reply:', error);
                    alert(this._translate('messages_send_error') + ': ' + error.message);
                } finally {
                    submitBtn.disabled = false;
                    submitBtn.textContent = this._translate('feedback_modal_send');
                }
            });
        }
        
        // Clear textarea when modal is hidden
        modalEl.addEventListener('hidden.bs.modal', function() {
            const textarea = modalEl.querySelector('#reply-message-text');
            if (textarea) textarea.value = '';
            delete modalEl.dataset.messageId;
        });
        
        return modalEl;
    }
    
    /**
     * Open reply modal
     */
    _openReplyModal(messageId, notificationId, replyToId = null) {
        if (!messageId) return;
        
        const modalEl = this._ensureReplyModal();
        modalEl.dataset.messageId = messageId;
        if (notificationId) {
            modalEl.dataset.notificationId = notificationId;
        }
        modalEl.dataset.replyToId = replyToId || '';
        
        if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
            const modal = new bootstrap.Modal(modalEl);
            modal.show();
        }
    }
    
    /**
     * Show success toast (reuse feedback modal style)
     */
    _showSuccessToast(title, text) {
        // Reuse feedback modal toast function if available
        if (window.Messages && window.Messages.showSuccessToast) {
            window.Messages.showSuccessToast(title, text);
            return;
        }
        
        // Fallback: create toast manually
        const container = this.container || 
                         document.getElementById('feedback-toast-container') ||
                         (() => {
                             const c = document.createElement('div');
                             c.id = 'feedback-toast-container';
                             c.className = 'position-fixed top-0 end-0 p-3';
                             c.style.zIndex = '1060';
                             c.style.maxWidth = '400px';
                             document.body.appendChild(c);
                             return c;
                         })();
        
        const id = 'toast-' + Date.now();
        const html = `
            <div id="${id}" class="toast mb-2 align-items-center border-0" role="status" aria-live="polite" aria-atomic="true" data-bs-delay="3800">
                <div class="toast-header bg-success text-canvas">
                    <i class="bi bi-check-circle-fill me-2"></i>
                    <strong class="me-auto">${this._escapeHtml(title || 'Success')}</strong>
                    <small>now</small>
                    <button type="button" class="btn-close btn-close-white ms-2 mb-1" data-bs-dismiss="toast" aria-label="Close"></button>
                </div>
                <div class="toast-body">
                    ${this._escapeHtml(text || '')}
                </div>
            </div>`;
        
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        const toast = wrapper.firstElementChild;
        container.appendChild(toast);
        
        if (typeof bootstrap !== 'undefined' && bootstrap.Toast) {
            const bsToast = new bootstrap.Toast(toast);
            bsToast.show();
            toast.addEventListener('hidden.bs.toast', function() { toast.remove(); });
        } else {
            setTimeout(() => { if (toast && toast.parentNode) toast.remove(); }, 3800);
        }
    }
    
    /**
     * Cleanup
     */
    destroy() {
        // Remove all toast elements
        if (this.container) {
            this.container.innerHTML = '';
        }
        this.toasts.clear();
    }
}

